import { hasPermission } from '../auth/matching';
import {
  type CatalogEntity,
  actionsForEntity,
  actionDisplayName,
  entityDisplayName,
  entityGroupsForOrg,
} from '../auth/permission-catalog';
import type { RoleOrgType } from '../auth/role-templates';
import { permissionsForOrg } from '../auth/user-management';
import type { UserSchema } from '../types/auth.type';
import { normalizePermissions } from './permissions.helpers';

export interface EntityPermissionDisplay {
  entity: CatalogEntity;
  entityLabel: string;
  allActions: boolean;
  actions: string[];
}

export interface PermissionGroupDisplay {
  groupLabel: string;
  entities: EntityPermissionDisplay[];
}

export interface OrgPermissionsDisplay {
  fullOrgAccess: boolean;
  groups: PermissionGroupDisplay[];
  legacyRoles: string[];
}

export function flatOrgPermissions(user: UserSchema, orgId: string): string[] {
  const perms = user.data?.permissions;
  if (!Array.isArray(perms)) return [];
  return permissionsForOrg(perms, orgId);
}

export function legacyRolesForOrg(user: UserSchema, orgId: string): string[] {
  const perms = user.data?.permissions;
  if (Array.isArray(perms) || !perms) return [];
  const dict = normalizePermissions(perms);
  return (dict[orgId] ?? []).map(String);
}

export function groupOrgPermissionsForDisplay(
  orgPerms: readonly string[],
  orgId: string,
  orgType: RoleOrgType,
): Pick<OrgPermissionsDisplay, 'fullOrgAccess' | 'groups'> {
  const fullOrgAccess = hasPermission(orgPerms, `recyclee.${orgId}.*`);

  const groups: PermissionGroupDisplay[] = [];

  for (const group of entityGroupsForOrg(orgType)) {
    const entities: EntityPermissionDisplay[] = [];

    for (const entity of group.entities) {
      const entityWildcard = `recyclee.${orgId}.*.${entity}.*`;
      const allActions = fullOrgAccess || hasPermission(orgPerms, entityWildcard);

      if (allActions) {
        entities.push({
          entity,
          entityLabel: entityDisplayName(entity),
          allActions: true,
          actions: [],
        });
        continue;
      }

      const grantedActions = actionsForEntity(entity).filter((action) =>
        hasPermission(orgPerms, `recyclee.${orgId}.*.${entity}.${action}`),
      );

      if (grantedActions.length === 0) continue;

      entities.push({
        entity,
        entityLabel: entityDisplayName(entity),
        allActions: false,
        actions: grantedActions.map(actionDisplayName),
      });
    }

    if (entities.length > 0) {
      groups.push({ groupLabel: group.label, entities });
    }
  }

  return { fullOrgAccess, groups };
}

export function buildOrgPermissionsDisplay(
  user: UserSchema,
  orgId: string,
  orgType: RoleOrgType,
): OrgPermissionsDisplay {
  const legacyRoles = legacyRolesForOrg(user, orgId);
  const orgPerms = flatOrgPermissions(user, orgId);
  const { fullOrgAccess, groups } = groupOrgPermissionsForDisplay(orgPerms, orgId, orgType);
  return { fullOrgAccess, groups, legacyRoles };
}
